import { useEffect, useState } from 'react';
import {
  ChevronRight,
  Code2,
  ExternalLink,
  FlaskConical,
  LucideIcon,
  PanelTop,
} from 'lucide-react';
import { useIsAllowed } from '@/app/navigation/access';
import { createLink } from '@/app/navigation/stack-links';
import { isDevMenuVisible, subscribeDevMenu } from '@/config/devMenu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/Sidebar/sidebar';

const SidebarMenuSubLink = createLink(SidebarMenuSubButton);

interface DevSubItem {
  title: string;
  url: string;
  /** Use <a> tag instead of router link (for /demos which has separate router) */
  external?: boolean;
}

interface DevItem {
  title: string;
  icon: LucideIcon;
  items: DevSubItem[];
}

const devItems: DevItem[] = [
  {
    title: 'Test Panels',
    icon: PanelTop,
    items: [
      { title: 'Panel State', url: '/panel-state-test' },
      { title: 'Error Panels', url: '/error/error' },
    ],
  },
  {
    title: 'Demos',
    icon: FlaskConical,
    items: [
      { title: 'Demo Hub', url: '/demos', external: true },
      { title: 'Forms', url: '/demos/forms', external: true },
      { title: 'Data Table', url: '/demos/table', external: true },
      { title: 'Routing', url: '/demos/routing', external: true },
      { title: 'PostgREST', url: '/demos/postgrest', external: true },
      {
        title: 'Tokenized Input',
        url: '/demos/tokenized-input',
        external: true,
      },
      { title: 'Editor', url: '/demos/editor', external: true },
      { title: 'Checkbox', url: '/demos/checkbox', external: true },
    ],
  },
  {
    title: 'Links',
    icon: ExternalLink,
    items: [
      { title: 'Day Planning Demo', url: '/day-planning/2026-01-01' },
      { title: 'Multi-Tech Demo', url: '/day-planning/2026-01-15' },
      { title: 'My Workorders', url: '/my-workorders/2026-01-15' },
      { title: 'My Hours Demo', url: '/my-hours/2026-W01' },
    ],
  },
];

export function NavDev() {
  const [visible, setVisible] = useState(isDevMenuVisible);
  const { internal } = useIsAllowed();

  useEffect(() => {
    return subscribeDevMenu(setVisible);
  }, []);

  // Never for external accounts, whatever the localStorage toggle says.
  if (!visible || !internal) return null;

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="text-orange-600 dark:text-orange-400">
        <Code2 className="mr-1 inline-block size-3" />
        Developer
      </SidebarGroupLabel>
      <SidebarMenu>
        {devItems.map((item) => (
          <Collapsible
            key={item.title}
            asChild
            defaultOpen={false}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <div className="flex">
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton tooltip={item.title}>
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                    <ChevronRight
                      className="ml-auto transition-transform duration-200
                        group-data-[state=open]/collapsible:rotate-90"
                    />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent>
                <SidebarMenuSub>
                  {item.items.map((subItem) =>
                    subItem.external ? (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton asChild>
                          <a
                            href={subItem.url}
                            target={
                              subItem.url.startsWith('http')
                                ? '_blank'
                                : undefined
                            }
                            rel={
                              subItem.url.startsWith('http')
                                ? 'noopener noreferrer'
                                : undefined
                            }
                          >
                            <span>{subItem.title}</span>
                          </a>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ) : (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubLink to={subItem.url}>
                          <span>{subItem.title}</span>
                        </SidebarMenuSubLink>
                      </SidebarMenuSubItem>
                    ),
                  )}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
